import React, { useState } from 'react';
import { format, subDays, subWeeks, subMonths, startOfWeek, startOfMonth } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon, BookOpenIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useDays } from '../hooks/useDays';
import { useWeeks } from '../hooks/useWeeks';
import { useMonths } from '../hooks/useMonths';
import type { Todo } from '../types';

type TimelineType = 'days' | 'weeks' | 'months';

interface TimelineViewProps {
  onTaskClick?: (task: Todo) => void;
}

export const TimelineView: React.FC<TimelineViewProps> = ({ onTaskClick }) => {
  const [timelineType, setTimelineType] = useState<TimelineType>('days');
  const [currentPeriod, setCurrentPeriod] = useState(new Date());
  
  const { currentDay, completedTasks: dayCompletedTasks } = useDays(currentPeriod);
  const { currentWeek, completedTasks: weekCompletedTasks } = useWeeks(currentPeriod);
  const { currentMonth, completedTasks: monthCompletedTasks } = useMonths(currentPeriod);
  
  // Get the appropriate data based on timeline type
  const currentData = timelineType === 'days' ? currentDay : 
                     timelineType === 'weeks' ? currentWeek : currentMonth;
  const completedTasks = timelineType === 'days' ? dayCompletedTasks : 
                        timelineType === 'weeks' ? weekCompletedTasks : monthCompletedTasks;

  const navigatePeriod = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentPeriod);
    
    if (timelineType === 'days') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (timelineType === 'weeks') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else if (timelineType === 'months') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    }
    
    setCurrentPeriod(newDate);
  };

  const goToToday = () => {
    setCurrentPeriod(new Date());
  };

  const formatPeriod = () => {
    if (timelineType === 'days') {
      return format(currentPeriod, 'EEEE, MMMM d, yyyy');
    } else if (timelineType === 'weeks') {
      const weekStart = startOfWeek(currentPeriod);
      return `Week of ${format(weekStart, 'MMMM d, yyyy')}`;
    } else {
      return format(startOfMonth(currentPeriod), 'MMMM yyyy');
    }
  };

  const isToday = () => {
    const today = new Date();
    if (timelineType === 'days') {
      return currentPeriod.toDateString() === today.toDateString();
    } else if (timelineType === 'weeks') {
      const weekStart = startOfWeek(currentPeriod);
      const todayWeekStart = startOfWeek(today);
      return weekStart.toDateString() === todayWeekStart.toDateString();
    } else {
      return currentPeriod.getMonth() === today.getMonth() && 
             currentPeriod.getFullYear() === today.getFullYear();
    }
  };

  const getQuickJumpDates = () => {
    const today = new Date();
    const dates = [];
    
    for (let i = 0; i < 7; i++) {
      if (timelineType === 'days') {
        dates.push(subDays(today, i));
      } else if (timelineType === 'weeks') {
        dates.push(subWeeks(today, i));
      } else {
        dates.push(subMonths(today, i));
      }
    }
    
    return dates;
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          {/* Timeline Type Selector */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {(['days', 'weeks', 'months'] as TimelineType[]).map((type) => (
              <button
                key={type}
                onClick={() => {
                  setTimelineType(type);
                  setCurrentPeriod(new Date()); // Reset to current when changing type
                }}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  timelineType === type
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigatePeriod('prev')}
              className="p-2 rounded-md hover:bg-gray-100"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>

            <div className="flex items-center space-x-2 min-w-0">
              <CalendarIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <span className="font-medium text-gray-900 truncate">
                {formatPeriod()}
              </span>
              {isToday() && (
                <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full flex-shrink-0">
                  Current
                </span>
              )}
            </div>

            <button
              onClick={() => navigatePeriod('next')}
              className="p-2 rounded-md hover:bg-gray-100"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        <button
          onClick={goToToday}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
        >
          Go to Today
        </button>
      </div>

      {/* Quick Jump Bar */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Quick Jump</h3>
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {getQuickJumpDates().map((date, index) => {
            const isSelected = timelineType === 'days' 
              ? date.toDateString() === currentPeriod.toDateString()
              : timelineType === 'weeks'
              ? startOfWeek(date).toDateString() === startOfWeek(currentPeriod).toDateString()
              : date.getMonth() === currentPeriod.getMonth() && date.getFullYear() === currentPeriod.getFullYear();

            return (
              <button
                key={index}
                onClick={() => setCurrentPeriod(date)}
                className={`flex-shrink-0 px-3 py-2 rounded-md text-sm transition-colors ${
                  isSelected
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {timelineType === 'days' && format(date, 'MMM d')}
                {timelineType === 'weeks' && `Week of ${format(startOfWeek(date), 'MMM d')}`}
                {timelineType === 'months' && format(date, 'MMM yyyy')}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content Area */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Summary Stats */}
        <div className="md:col-span-1">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <ClockIcon className="h-5 w-5 mr-2 text-gray-500" />
              {timelineType === 'days' ? 'Day' : timelineType === 'weeks' ? 'Week' : 'Month'} Summary
            </h3>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Completed Tasks</span>
                <span className="font-semibold text-green-600">
                  {completedTasks?.length || 0}
                </span>
              </div>
              
              {currentData && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">
                      {timelineType === 'days' ? 'Journal Entry' : 
                       timelineType === 'weeks' ? 'Weekly Review' : 'Monthly Review'}
                    </span>
                    <span className={`font-semibold ${currentData.is_review_completed ? 'text-green-600' : 'text-gray-400'}`}>
                      {currentData.is_review_completed ? 'Complete' : 'Incomplete'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Goals Set</span>
                    <span className="font-semibold text-blue-600">
                      {currentData.goals?.length || 0}
                    </span>
                  </div>
                  
                  {timelineType === 'days' && currentDay && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Gratitude Items</span>
                      <span className="font-semibold text-yellow-600">
                        {currentDay.gratitude?.length || 0}
                      </span>
                    </div>
                  )}
                  
                  {timelineType === 'months' && currentMonth && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Projects</span>
                      <span className="font-semibold text-purple-600">
                        {currentMonth.projects?.length || 0}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Detailed Content */}
        <div className="md:col-span-2">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <BookOpenIcon className="h-5 w-5 mr-2 text-gray-500" />
              {timelineType === 'days' ? 'Daily' : timelineType === 'weeks' ? 'Weekly' : 'Monthly'} Review
            </h3>
            
            {currentData ? (
              <div className="space-y-6">
                {/* Goals */}
                {currentData.goals && currentData.goals.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Goals</h4>
                    <ul className="space-y-1">
                      {currentData.goals.map((goal, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                          <span className="text-gray-700">{goal}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Accomplishments - Days only */}
                {timelineType === 'days' && currentDay?.accomplishments && currentDay.accomplishments.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Accomplishments</h4>
                    <ul className="space-y-1">
                      {currentDay.accomplishments.map((item, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <span className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></span>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Projects - Months only */}
                {timelineType === 'months' && currentMonth?.projects && currentMonth.projects.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Projects</h4>
                    <ul className="space-y-1">
                      {currentMonth.projects.map((project, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <span className="flex-shrink-0 w-2 h-2 bg-purple-500 rounded-full mt-2"></span>
                          <span className="text-gray-700">{project}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Reflections */}
                {currentData.reflections && Array.isArray(currentData.reflections) && currentData.reflections.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Reflections</h4>
                    <div className="space-y-3">
                      {currentData.reflections
                        .filter((entry: { prompt: string; response: string }) => entry.response?.trim())
                        .map((entry: { prompt: string; response: string }, index: number) => (
                        <div key={index} className="bg-gray-50 p-3 rounded-md">
                          <p className="text-sm text-gray-600 mb-1">{entry.prompt}</p>
                          <p className="text-gray-800">{entry.response}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {currentData.review_notes && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                    <div className="bg-gray-50 p-3 rounded-md">
                      <p className="text-gray-800 whitespace-pre-wrap">{currentData.review_notes}</p>
                    </div>
                  </div>
                )}

                {/* Completed Tasks */}
                {completedTasks && completedTasks.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Completed Tasks</h4>
                    <div className="space-y-2">
                      {completedTasks.map((task) => (
                        <div 
                          key={task.id} 
                          className={`flex items-start space-x-2 p-2 bg-green-50 rounded-md transition-all ${
                            onTaskClick ? 'cursor-pointer hover:bg-green-100 hover:shadow-md' : ''
                          }`}
                          onClick={() => onTaskClick?.(task)}
                        >
                          <span className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></span>
                          <span className="text-gray-700 line-through">{task.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <BookOpenIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>
                  {timelineType === 'days' 
                    ? 'No journal entry for this day' 
                    : `No ${timelineType === 'weeks' ? 'weekly' : 'monthly'} review for this period`
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};